<template>
  <div class="awe">
    <div v-if="!disabled" class="d-flex flex-column" style="gap: 6px">
      <span class="acq-label">Vorlagen</span>
      <div class="pills" role="group" aria-label="Vorlagen">
        <button v-for="preset in presets" :key="preset.id" :title="`${preset.text} (${preset.hint}) zu Mo–Fr hinzufügen`" class="pill awe__pill"
                type="button" @click="applyPreset(preset)">
          <v-icon class="pill__icon" x-small>mdi-lightning-bolt-outline</v-icon>{{ preset.text }}<span class="awe__hint">{{ preset.hint }}</span>
        </button>
        <button :disabled="!value.length" class="pill awe__pill awe__pill--danger" type="button" @click="clearAll">
          <v-icon class="pill__icon" x-small>mdi-eraser</v-icon>Alles leeren
        </button>
      </div>
    </div>

    <div v-if="withLabels && !disabled" class="d-flex flex-column" style="gap: 6px">
      <span class="acq-label">Markierung für neue Zeitfenster</span>
      <div class="pills" role="group" aria-label="Markierung für neue Zeitfenster">
        <button v-for="label in labels" :key="label" :aria-pressed="String(defaultLabels.includes(label))"
                :class="{ 'pill--active': defaultLabels.includes(label) }" class="pill awe__pill" type="button" @click="toggleDefaultLabel(label)">
          <span :style="{ background: labelColor(label) }" class="awe__dot"/>{{ labelText(label) }}
        </button>
      </div>
    </div>

    <v-expand-transition>
      <div v-if="undo" class="awe__undo">
        <v-icon small>mdi-information-outline</v-icon>
        <span class="flex-grow-1">{{ undo.text }}</span>
        <button class="add-link" type="button" @click="revert"><v-icon x-small>mdi-undo</v-icon>Rückgängig</button>
      </div>
    </v-expand-transition>

    <div class="awe__week">
      <div v-if="!value.length" class="awe__empty">
        <v-icon small>mdi-calendar-blank-outline</v-icon>
        <span>{{ emptyText }}</span>
      </div>
      <div v-if="$vuetify.breakpoint.smAndUp" aria-hidden="true" class="awe__scale">
        <span/>
        <span/>
        <span class="awe__scale-bar">
          <span v-for="hour in scaleHours" :key="hour" :style="{ left: `${((hour - 6) / 14) * 100}%` }">{{ hour }}</span>
        </span>
        <span/>
      </div>
      <availability-day-row v-for="day in visibleDays" :key="day" :day="day" :days="visibleDays" :disabled="disabled" :editing-index="editingIndex"
                            :entries="entriesByDay[day]" :labels="labels" @add="addRange(day)" @copy="copyDay(day, $event)"
                            @edit="editingIndex = $event" @remove="removeAt" @update="updateAt"/>
      <button v-if="!hasWeekendEntries" class="add-link awe__weekend" type="button" @click="showWeekend = !showWeekend">
        <v-icon x-small>{{ showWeekend ? 'mdi-chevron-up' : 'mdi-plus' }}</v-icon>
        {{ showWeekend ? 'Wochenende ausblenden' : 'Wochenende einblenden (Sa, So)' }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import AvailabilityDayRow, {DayEntry} from './availabilityDayRow.vue';
import {
  ALL_DAYS, ALL_LABELS, DEFAULT_LABELS, EditableAvailability, PRESETS, WORKDAYS, applyPreset, availabilitySummary, cloneAvailability, copyDay,
  dayShort, labelColor, labelText, nextRange, validateAvailabilities,
} from './availabilityEditor.helper';

type Preset = typeof PRESETS[number];

/**
 * Presentational week editor of weekly availabilities (v-model: EditableAvailability[]).
 * Rows Mo–So with editable range chips, presets, copy to other days and inline validation (helper: validateAvailabilities).
 */
export default Vue.extend({
  components: {AvailabilityDayRow},
  props: {
    value: {type: Array as () => EditableAvailability[], default: () => []},
    /** Allowed label values; an empty array hides labels completely (entities without labels, e.g. students) */
    labels: {type: Array as () => string[], default: () => ALL_LABELS},
    disabled: {type: Boolean, default: false},
    emptyText: {type: String, default: 'Noch keine Verfügbarkeiten – nutze eine Vorlage oder füge Zeitfenster hinzu'},
  },
  data: () => ({
    editingIndex: -1,
    defaultLabels: [...DEFAULT_LABELS] as string[],
    showWeekend: false,
    undo: null as null | { items: EditableAvailability[]; text: string },
  }),
  computed: {
    presets: () => PRESETS,
    scaleHours: () => [6, 8, 10, 12, 14, 16, 18, 20],
    withLabels(): boolean {
      return this.labels.length > 0;
    },
    hasWeekendEntries(): boolean {
      return this.value.some((item) => !WORKDAYS.includes(item.dayOfWeek));
    },
    visibleDays(): number[] {
      return this.showWeekend || this.hasWeekendEntries ? ALL_DAYS : WORKDAYS;
    },
    issues(): Record<number, string> {
      const result: Record<number, string> = {};
      validateAvailabilities(this.value, this.withLabels).forEach((issue) => {
        result[issue.index] = issue.message;
      });
      return result;
    },
    entriesByDay(): Record<number, DayEntry[]> {
      const result: Record<number, DayEntry[]> = {};
      ALL_DAYS.forEach((day) => {
        result[day] = [];
      });
      this.value.forEach((item, index) => {
        if (!result[item.dayOfWeek]) {
          result[item.dayOfWeek] = [];
        }
        result[item.dayOfWeek].push({index, item, issue: this.issues[index]});
      });
      Object.values(result).forEach((entries) => entries.sort((a, b) => a.item.timeRange.startTime - b.item.timeRange.startTime));
      return result;
    },
    newLabels(): string[] | undefined {
      if (!this.withLabels) {
        return undefined;
      }
      const allowed = this.defaultLabels.filter((label) => this.labels.includes(label));
      return allowed.length ? allowed : [this.labels[0]];
    },
  },
  methods: {
    labelColor,
    labelText,
    emitValue(items: EditableAvailability[]) {
      this.$emit('input', items);
    },
    remember(text: string) {
      this.undo = {items: this.value.map(cloneAvailability), text};
    },
    revert() {
      if (this.undo) {
        this.editingIndex = -1;
        this.emitValue(this.undo.items);
        this.undo = null;
      }
    },
    toggleDefaultLabel(label: string) {
      const next = this.defaultLabels.includes(label) ? this.defaultLabels.filter((el) => el !== label) : [...this.defaultLabels, label];
      this.defaultLabels = next.length ? next : [label];
    },
    addRange(day: number) {
      const item: EditableAvailability = {dayOfWeek: day, timeRange: nextRange(this.value, day)};
      if (this.newLabels) {
        item.labels = [...this.newLabels];
      }
      this.undo = null;
      this.emitValue([...this.value, item]);
      this.editingIndex = this.value.length;
    },
    removeAt(index: number) {
      const removed = this.value[index];
      this.remember(`Zeitfenster ${dayShort(removed.dayOfWeek)} entfernt`);
      this.editingIndex = -1;
      this.emitValue(this.value.filter((_, i) => i !== index));
    },
    updateAt({index, item}: { index: number; item: EditableAvailability }) {
      this.emitValue(this.value.map((el, i) => (i === index ? item : el)));
    },
    applyPreset(preset: Preset) {
      const next = applyPreset(this.value, preset.start, preset.end, this.newLabels);
      const added = next.length - this.value.length;
      this.remember(added
        ? `${preset.text}: ${added} Zeitfenster hinzugefügt`
        : `${preset.text}: nichts hinzugefügt, die Tage haben schon überschneidende Zeitfenster`);
      this.editingIndex = -1;
      this.emitValue(next);
    },
    clearAll() {
      this.remember(`${availabilitySummary(this.value)} geleert`);
      this.editingIndex = -1;
      this.emitValue([]);
    },
    copyDay(source: number, targets: number[]) {
      this.remember(`${dayShort(source)} auf ${targets.map(dayShort).join(', ')} kopiert`);
      this.editingIndex = -1;
      if (targets.some((day) => !WORKDAYS.includes(day))) {
        this.showWeekend = true;
      }
      this.emitValue(copyDay(this.value, source, targets));
    },
  },
  watch: {
    value(items: EditableAvailability[]) {
      if (this.editingIndex >= items.length) {
        this.editingIndex = -1;
      }
    },
  },
});
</script>

<style scoped>
.awe {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.awe__pill {
  height: 30px;
  font-size: 12px;
}

.awe__pill--danger:not(:disabled):hover {
  background: #FDECEC;
  color: #B71C1C;
}

.awe__pill:disabled {
  cursor: default;
  opacity: .5;
}

.awe__hint {
  font-weight: 400;
  opacity: .65;
}

.awe__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex: none;
}

.awe__undo {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, .04);
  font-size: 13px;
}

.awe__week {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, .08);
}

.awe__empty {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 4px 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #E8F1FD;
  color: #0D47A1;
  font-size: 13px;
}

.awe__empty .v-icon {
  color: inherit !important;
}

.awe__scale {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 160px 28px;
  gap: 10px;
  padding: 2px 10px 0;
}

.awe__scale-bar {
  position: relative;
  height: 14px;
  font-size: 10px;
  color: rgba(15, 23, 42, .45);
  font-variant-numeric: tabular-nums;
}

.awe__scale-bar span {
  position: absolute;
  transform: translateX(-50%);
}

.awe__scale-bar span:first-child {
  transform: none;
}

.awe__scale-bar span:last-child {
  transform: translateX(-100%);
}

.awe__weekend {
  margin: 6px 10px 4px;
  font-size: 12px;
}

.theme--dark .awe__week {
  border-color: rgba(255, 255, 255, .1);
}

.theme--dark .awe__undo {
  background: rgba(255, 255, 255, .06);
}

.theme--dark .awe__empty {
  background: rgba(100, 181, 246, .16);
  color: #90CAF9;
}

.theme--dark .awe__scale-bar {
  color: rgba(255, 255, 255, .45);
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
